import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { QRCodeCanvas } from "qrcode.react";
import { Circle, Loader2, Link2, ArrowRight, Sparkles, Users, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const L = {
  fr: { eyebrow: "Le Cercle", title: "Reliez-vous, en direct", subtitle: "Une question, deux à cinq esprits, en temps réel. L'Élan tisse le lien entre vous.",
    qLabel: "La question du Cercle", qPlaceholder: "Ex. : Faut-il tout dire à ceux qu'on aime ?", open: "Ouvrir le Cercle", opening: "Ouverture…",
    invite: "Fais scanner ce QR, ou partage le code / le lien.", code: "Code du Cercle", copy: "Copier le lien", copied: "Lien copié !", enter: "Entrer dans le Cercle",
    joinTitle: "Rejoindre un Cercle", joinPlaceholder: "CODE À 6 CARACTÈRES", join: "Rejoindre", joining: "…", notfound: "Aucun Cercle actif avec ce code.",
    trial: "Essai gratuit de 7 jours — puis Le Cocoon.", hint: "2 à 5 personnes · éphémère · un Sceau à garder à la fin." },
  en: { eyebrow: "The Circle", title: "Connect, live", subtitle: "One question, two to five minds, in real time. L'Élan weaves the bond between you.",
    qLabel: "The Circle's question", qPlaceholder: "e.g. Should we tell everything to those we love?", open: "Open the Circle", opening: "Opening…",
    invite: "Let them scan this QR, or share the code / link.", code: "Circle code", copy: "Copy link", copied: "Link copied!", enter: "Enter the Circle",
    joinTitle: "Join a Circle", joinPlaceholder: "6-CHARACTER CODE", join: "Join", joining: "…", notfound: "No active Circle with this code.",
    trial: "7-day free trial — then The Cocoon.", hint: "2 to 5 people · ephemeral · a Seal to keep at the end." },
  es: { eyebrow: "El Círculo", title: "Conéctense, en directo", subtitle: "Una pregunta, de dos a cinco mentes, en tiempo real. L'Élan teje el vínculo entre ustedes.",
    qLabel: "La pregunta del Círculo", qPlaceholder: "Ej.: ¿Hay que decirlo todo a quienes amamos?", open: "Abrir el Círculo", opening: "Abriendo…",
    invite: "Que escaneen este QR, o comparte el código / enlace.", code: "Código del Círculo", copy: "Copiar enlace", copied: "¡Enlace copiado!", enter: "Entrar al Círculo",
    joinTitle: "Unirse a un Círculo", joinPlaceholder: "CÓDIGO DE 6 CARACTERES", join: "Unirse", joining: "…", notfound: "Ningún Círculo activo con este código.",
    trial: "Prueba gratuita de 7 días — luego El Cocoon.", hint: "2 a 5 personas · efímero · un Sello para guardar al final." },
  nl: { eyebrow: "De Cirkel", title: "Verbind je, live", subtitle: "Eén vraag, twee tot vijf geesten, in realtime. L'Élan weeft de band tussen jullie.",
    qLabel: "De vraag van de Cirkel", qPlaceholder: "Bv.: Moeten we alles zeggen tegen wie we liefhebben?", open: "Open de Cirkel", opening: "Openen…",
    invite: "Laat deze QR scannen, of deel de code / link.", code: "Cirkelcode", copy: "Link kopiëren", copied: "Link gekopieerd!", enter: "Ga de Cirkel binnen",
    joinTitle: "Word lid van een Cirkel", joinPlaceholder: "CODE VAN 6 TEKENS", join: "Meedoen", joining: "…", notfound: "Geen actieve Cirkel met deze code.",
    trial: "7 dagen gratis proberen — daarna The Cocoon.", hint: "2 tot 5 personen · vluchtig · een Zegel om te bewaren." },
};

export default function CercleLaunch() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const lang = (i18n.language || "fr").slice(0, 2);
  const el = L[lang] || L.fr;
  const isCocon = !!(user && (user.role === "admin" || user.plan === "cocon"));

  const [question, setQuestion] = useState("");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null); // { id, code, link }
  const [code, setCode] = useState("");
  const [joining, setJoining] = useState(false);

  const create = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const { data } = await api.post("/cercle/create", { question: question.trim(), lang });
      localStorage.setItem(`coccon_cercle_${data.id}`, JSON.stringify({ pid: data.participant_id, name: data.name, isHost: true }));
      const link = `${window.location.origin}/cercle/${data.id}`;
      setCreated({ id: data.id, code: data.code, link });
    } catch (e) {
      if (e.response?.status === 402) toast.error(formatApiErrorDetail(e.response?.data?.detail));
      else toast.error(formatApiErrorDetail(e.response?.data?.detail) || "…");
    } finally {
      setCreating(false);
    }
  };

  const joinByCode = async () => {
    const c = code.trim().toUpperCase();
    if (c.length < 6 || joining) return;
    setJoining(true);
    try {
      const { data } = await api.get(`/cercle/by-code/${c}`);
      navigate(`/cercle/${data.id}`);
    } catch {
      toast.error(el.notfound);
    } finally {
      setJoining(false);
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(created.link); } catch { /* ignore */ }
    toast.success(el.copied);
  };

  return (
    <div className="h-full overflow-y-auto scroll-thin">
      <div className="max-w-2xl mx-auto px-6 py-14">
        <div className="flex items-center gap-3 justify-center mb-3">
          <Circle className="text-primary" size={20} />
          <span className="uppercase tracking-[0.22em] text-xs text-primary/90">{el.eyebrow}</span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl text-gradient-orange text-center leading-tight mb-4">{el.title}</h1>
        <p className="text-foreground/60 text-center leading-relaxed mb-10 max-w-lg mx-auto">{el.subtitle}</p>

        {!created ? (
          <>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-8 frame-pulse" data-testid="cercle-create-card">
              <label className="block text-sm text-foreground/70 mb-3">{el.qLabel}</label>
              <textarea
                data-testid="cercle-question-input"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                rows={2}
                placeholder={el.qPlaceholder}
                className="w-full bg-transparent border border-white/15 rounded-2xl px-4 py-3 outline-none text-foreground placeholder:text-foreground/30 resize-none focus:border-primary/50 transition-colors"
              />
              <button
                data-testid="cercle-create-btn"
                onClick={create}
                disabled={creating}
                className="btn-frost-orange w-full mt-6 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-base disabled:opacity-50"
              >
                {creating ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                {creating ? el.opening : el.open}
              </button>
              <p className="text-xs text-foreground/40 text-center mt-4 leading-relaxed">{el.hint}</p>
              {!isCocon && <p className="text-[11px] text-primary/70 text-center mt-2">{el.trial}</p>}
            </motion.div>

            <div className="glass rounded-3xl p-6 mt-5" data-testid="cercle-join-card">
              <div className="flex items-center gap-2 mb-3 text-sm text-foreground/70">
                <KeyRound size={15} className="text-primary" /> {el.joinTitle}
              </div>
              <div className="flex items-center gap-2">
                <input
                  data-testid="cercle-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6))}
                  onKeyDown={(e) => e.key === "Enter" && joinByCode()}
                  placeholder={el.joinPlaceholder}
                  className="flex-1 bg-black/30 border border-white/15 rounded-full px-5 py-3 text-center tracking-[0.3em] font-semibold outline-none text-foreground placeholder:text-foreground/25 placeholder:tracking-normal focus:border-primary/50 transition-colors"
                />
                <button
                  data-testid="cercle-join-btn"
                  onClick={joinByCode}
                  disabled={joining || code.length < 6}
                  className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm disabled:opacity-40 flex-shrink-0"
                >
                  {joining ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                </button>
              </div>
            </div>
          </>
        ) : (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-8 frame-pulse text-center" data-testid="cercle-created-card">
            <p className="text-foreground/70 leading-relaxed mb-6">{el.invite}</p>
            <div className="bg-white rounded-2xl p-4 inline-block mb-6">
              <QRCodeCanvas value={created.link} size={190} bgColor="#ffffff" fgColor="#0b0e14" level="M" includeMargin={false} data-testid="cercle-qr" />
            </div>
            <div className="mb-6">
              <div className="text-[11px] uppercase tracking-[0.2em] text-foreground/40 mb-1">{el.code}</div>
              <div className="font-display text-4xl text-gradient-orange tracking-[0.35em]" data-testid="cercle-code-display">{created.code}</div>
            </div>
            <div className="flex items-center gap-2 bg-black/30 border border-white/10 rounded-full px-4 py-2.5 mb-6">
              <input readOnly value={created.link} data-testid="cercle-link-field" className="flex-1 bg-transparent text-sm text-foreground/70 outline-none truncate" />
              <button data-testid="cercle-copy-btn" onClick={copyLink} className="text-primary hover:text-primary/80 transition-colors flex-shrink-0" title={el.copy}>
                <Link2 size={18} />
              </button>
            </div>
            <button
              data-testid="cercle-enter-btn"
              onClick={() => navigate(`/cercle/${created.id}`)}
              className="btn-frost-orange w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-base"
            >
              <Users size={18} /> {el.enter}
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
